import Link from '@mui/material/Link';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Button, CircularProgress, IconButton, styled } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from '@/hooks';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as state from '@/state';
import { ComplexityChip } from '@/components/shared/ComplexityChip';
import { PageLoader } from '@/components/shared/PageLoader';
import {
  ZoneStatus,
  useUpdateZoneStatusMutation,
  ZoneAndHistory,
  CreatedByDetails,
  Maybe,
  History,
  useSphericalsPerZoneQuery,
  SphericalsPerZone,
} from '@/__generated__/graphql';
import { ZoneStatusTimeline } from '../ZoneStatusTimeline/ZoneStatusTimeline';
import { getAssigneeFromHistory } from '../common';
import { AssigneeAvatar } from '@/components/ZonesManagementRevamped/AssigneeAvatar';
import { SortableField } from '@/types';
import { getNextSort, splitSort } from '@/utils';

const BlackBgTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    textTransform: 'uppercase',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '1.4rem',
  },
}));

const AssignToMyselfButton = ({ zoneId }: { zoneId: string }) => {
  const [mutate, { loading }] = useUpdateZoneStatusMutation({
    refetchQueries: ['zoneAndHistoriesForZoneManagement'],
  });

  const styles = {
    textTransform: 'none',
  };

  const assignToMe = () => {
    mutate({
      variables: {
        input: {
          zoneId,
          status: ZoneStatus.InProgress,
        },
      },
    }).then();
  };
  return (
    <Button sx={styles} onClick={assignToMe} disabled={loading}>
      Assign to myself
      {loading && <CircularProgress size={20} />}
    </Button>
  );
};

type Props = {
  zones: Array<ZoneAndHistory>;
};

type Row = {
  id: string;
  zoneName: string;
  parts: number;
  points: number;
  sphericals: number;
  priority: string;
  assignee: CreatedByDetails | undefined;
  status: Maybe<ZoneStatus> | undefined;
  histories: Maybe<History>[];
};

const getSortedRows = (rows: Row[], currentSort: SortableField) => {
  if (!currentSort) return rows;
  const [sortType, sortDirection] = splitSort(currentSort);

  return [...rows].sort((a, b) => {
    if (sortDirection === 'asc') {
      return a[sortType] - b[sortType];
    }
    return b[sortType] - a[sortType];
  });
};

export const ZonesListViewer = ({ zones }: Props) => {
  const router = useRouter();

  const { inspection } = router.query;

  const zonesSearchTerm = useAtomValue(state.zonesSearchTerm);

  const [totalSphericals, setTotalSphericals] = useState<SphericalsPerZone[]>();

  const { data: sphericalsPerZoneData, loading } = useSphericalsPerZoneQuery({
    variables: {
      structureId: inspection?.toString(),
    },
  });

  useEffect(() => {
    if (sphericalsPerZoneData?.sphericalsPerZone) {
      setTotalSphericals(sphericalsPerZoneData.sphericalsPerZone ?? []);
    }
  }, [sphericalsPerZoneData?.sphericalsPerZone]);

  const rows = useMemo(() => {
    return zones.map((zone) => ({
      id: zone._id,
      zoneName: zone.name,
      parts: zone?.totalParts ?? 0,
      points: zone?.totalPoints ?? 0,
      sphericals: totalSphericals?.find((data) => data.zoneId === zone._id)?.totalSphericals ?? 0,
      priority: 'Low',
      assignee: getAssigneeFromHistory(zone),
      status: zone.status,
      histories: zone?.histories || [],
    }));
  }, [totalSphericals, zones]);

  const [visibleRows, setVisibleRows] = useState<Row[]>(rows);
  const [currentSort, setCurrentSort] = useState<SortableField>();

  const columns = [
    { field: 'id', headerName: 'ID', hide: true },
    { field: 'zoneName', headerName: 'Zone Name' },
    {
      field: 'parts',
      headerName: 'Parts',
      onClick: () => {
        setCurrentSort(getNextSort(currentSort, 'parts'));
      },
      icon: <SwapVertIcon />,
    },
    {
      field: 'points',
      headerName: 'Points',
      onClick: () => {
        setCurrentSort(getNextSort(currentSort, 'points'));
      },
      icon: <SwapVertIcon />,
    },
    {
      field: 'sphericals',
      headerName: 'Sphericals',
      onClick: () => {
        setCurrentSort(getNextSort(currentSort, 'sphericals'));
      },
      icon: <SwapVertIcon />,
    },
    { field: 'assignee', headerName: 'Assignee' },
    { field: 'difficulty', headerName: 'Difficulty' },
    {
      field: 'status',
      headerName: 'Status',
    },
  ];

  // Sorting
  useEffect(() => {
    if (!currentSort) return;

    setVisibleRows((previous) => getSortedRows(previous, currentSort));
  }, [currentSort]);

  // Searching
  useEffect(() => {
    // Filtering rows by search term
    const filteredRows = [...rows].filter((row) => {
      if (!row?.zoneName) return false;
      return row.zoneName.includes(zonesSearchTerm);
    });

    // Sorting filtered rows
    const sortedRows = getSortedRows([...filteredRows], currentSort);

    setVisibleRows(sortedRows);
  }, [currentSort, rows, zonesSearchTerm]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '25rem' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) =>
              column.hide ? undefined : (
                <BlackBgTableCell key={column.field} align="center">
                  {column.headerName}
                  <IconButton color="secondary" onClick={column?.onClick || undefined}>
                    {column.icon}
                  </IconButton>
                </BlackBgTableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell component="th" scope="row" align="center">
                <Link href={`/inspection/${inspection}?zone=${row.zoneName}&zoneid=${row.id}`}>
                  <Button variant="text">{row.zoneName}</Button>
                </Link>
              </TableCell>
              <TableCell align="center">{row.parts}</TableCell>
              <TableCell align="center">{row.points}</TableCell>
              <TableCell align="center">{row.sphericals}</TableCell>
              <TableCell align="center">
                {row.status === ZoneStatus.NotStarted ? (
                  <>{row?.id && <AssignToMyselfButton zoneId={row.id} />}</>
                ) : (
                  <AssigneeAvatar
                    assigneeName={row.assignee?.name}
                    profileImage={row.assignee?.profilePicture}
                  />
                )}
              </TableCell>
              <TableCell align="center">
                <ComplexityChip complexity="Low" />
              </TableCell>
              <TableCell align="right">
                {row?.status && (
                  <ZoneStatusTimeline status={row.status} histories={row?.histories || []} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
